import React from 'react';
import { Train, Bus, Radio, Accessibility, Navigation } from 'lucide-react';

/* Mode → lucide icon */
function ModeIcon({ mode, routeType }) {
  const m = (mode || '').toLowerCase();
  if (m.includes('bus') || m.includes('trolley')) return <Bus className="w-4 h-4" />;
  if (m.includes('ferry')) return <Navigation className="w-4 h-4" />;
  // Subway / metro / rail / tram all use train glyph
  return <Train className="w-4 h-4" />;
}

function fmtMinutes(m) {
  if (m == null) return '—';
  if (m <= 0) return 'Now';
  if (m === 1) return '1 min';
  return `${m} min`;
}

export default function TransitCard({ data, loading }) {
  if (loading) {
    return (
      <div className="puvio-card p-5" data-testid="transit-card-skeleton">
        <div className="puvio-skeleton h-5 w-28" />
        <div className="puvio-skeleton h-3 w-40 mt-2" />
        <div className="grid grid-cols-1 gap-2 mt-4">
          <div className="puvio-skeleton h-12 w-full" />
          <div className="puvio-skeleton h-12 w-full" />
        </div>
      </div>
    );
  }
  // Hide the entire card when the city isn't covered or we got nothing back.
  if (!data || !data.covered || !Array.isArray(data.routes) || data.routes.length === 0) {
    return null;
  }

  const routes = data.routes.slice(0, 4);
  const hasRealtime = routes.some(r => r.is_real_time);

  return (
    <div className="puvio-card p-5" data-testid="transit-card">
      <div className="flex items-start justify-between">
        <div>
          <p className="font-bold text-[11px] uppercase tracking-[0.22em] text-[var(--puvio-mint-deep)]">Transit</p>
          <p className="text-xs text-[var(--puvio-text-2)] mt-0.5 font-medium">
            {hasRealtime ? 'Live · Transit' : 'Schedule · Transit'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {hasRealtime && (
            <span
              className="inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded-full bg-[var(--puvio-sun)] text-[var(--puvio-sun-text)]"
              data-testid="transit-live-badge"
            >
              <Radio className="w-3 h-3" /> Live
            </span>
          )}
          <div className="w-12 h-12 rounded-2xl bg-mint-50 flex items-center justify-center" style={{ background: '#D2F4E5' }}>
            <Train className="w-6 h-6 text-[var(--puvio-mint-deep)]" />
          </div>
        </div>
      </div>

      <ul className="mt-4 flex flex-col gap-2" data-testid="transit-route-list">
        {routes.map((r) => {
          const next = r.departures && r.departures.length > 0 ? r.departures[0] : null;
          const swatchBg = r.color || '#1F4480';
          const swatchFg = r.text_color || '#FFFFFF';
          const minutes = next ? next.minutes_away : null;
          const isLive = next ? next.is_real_time : false;
          const wheelchairOk = r.stop?.wheelchair_boarding === 1;

          return (
            <li
              key={r.global_route_id || `${r.short_name}-${r.headsign}`}
              className="flex items-center gap-3 px-3 py-2.5 rounded-2xl bg-[var(--puvio-bg-soft)] border border-[var(--puvio-border)]"
              data-testid={`transit-route-${r.short_name}`}
            >
              {/* Route badge */}
              <div
                className="shrink-0 min-w-[44px] h-11 rounded-xl px-2 flex items-center justify-center font-extrabold text-sm tracking-tight shadow-soft"
                style={{ background: swatchBg, color: swatchFg }}
                title={r.long_name || r.short_name}
              >
                {r.short_name}
              </div>
              {/* Headsign + stop */}
              <div className="flex-1 min-w-0">
                <p
                  className="text-sm font-semibold text-[#0F1A13] truncate"
                  title={r.headsign}
                >
                  {r.headsign || r.long_name || r.mode}
                </p>
                <p className="text-[11px] text-[var(--puvio-text-2)] truncate flex items-center gap-1.5 font-medium">
                  <ModeIcon mode={r.mode} routeType={r.route_type} />
                  <span className="truncate">{r.stop?.name || r.mode}</span>
                  {r.stop?.distance_m != null && (
                    <span className="text-[var(--puvio-text-3)]">· {r.stop.distance_m}m</span>
                  )}
                  {wheelchairOk && (
                    <Accessibility
                      className="w-3 h-3 text-[var(--puvio-mint-deep)]"
                      aria-label="Wheelchair accessible stop"
                    />
                  )}
                </p>
              </div>
              {/* Next departure */}
              <div className="shrink-0 text-right">
                <p
                  className={`text-sm font-extrabold leading-none ${
                    minutes != null && minutes <= 1
                      ? 'text-[var(--puvio-coral-deep)]'
                      : 'text-[#0F1A13]'
                  }`}
                  data-testid={`transit-next-${r.short_name}`}
                >
                  {fmtMinutes(minutes)}
                </p>
                {isLive && (
                  <span className="inline-flex items-center gap-0.5 text-[10px] font-bold mt-1 text-[var(--puvio-mint-deep)]">
                    <span className="w-1.5 h-1.5 rounded-full bg-[var(--puvio-mint-deep)] animate-pulse" />
                    live
                  </span>
                )}
              </div>
            </li>
          );
        })}
      </ul>

      <p className="mt-3 text-[10px] text-[var(--puvio-text-3)] font-medium">
        Live public transit departures
      </p>
    </div>
  );
}
